import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

const ListDisplayTable = ({
    data = [],
    columns = [],
    sortable = false,
    selectable = false,
    multiSelect = false,
    defaultSort = null,
    onSort = () => { },
    onSelect = () => { },
    onRowClick = null,
    className = '',
    headerClassName = 'bg-gray-200',
    rowClassName = 'hover:bg-gray-100',
    cellClassName = 'p-3',
    title = '',
    stickyHeader = true
}) => {
    const [sortConfig, setSortConfig] = useState(defaultSort);
    const [selectedRows, setSelectedRows] = useState([]);

    const handleSort = (key) => {
        if (!sortable) return;

        let direction = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        const newSortConfig = { key, direction };
        setSortConfig(newSortConfig);
        onSort(newSortConfig);
    };

    const handleRowSelect = (rowId) => {
        if (!selectable) return;

        let newSelectedRows;
        if (multiSelect) {
            newSelectedRows = selectedRows.includes(rowId)
                ? selectedRows.filter(id => id !== rowId)
                : [...selectedRows, rowId];
        } else {
            newSelectedRows = selectedRows.includes(rowId) ? [] : [rowId];
        }

        setSelectedRows(newSelectedRows);
        onSelect(newSelectedRows);
    };

    const sortedData = useMemo(() => {
        if (!sortConfig || !sortConfig.key) return data;

        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
    }, [data, sortConfig]);

    const getSortIcon = (key) => {
        if (!sortConfig || sortConfig.key !== key) return <FaSort className="ml-1 opacity-30" />;
        return sortConfig.direction === 'ascending'
            ? <FaSortUp className="ml-1" />
            : <FaSortDown className="ml-1" />;
    };

    return (
        <div className={`bg-white rounded shadow overflow-x-auto ${className}`}>
            <span className={"px-2 py-1 font-bold"} >All {title}</span>

            <table className="min-w-full table-auto">

                <thead className={headerClassName}>
                    <tr>
                        {selectable && (
                            <th className={`${cellClassName} w-10`}>
                                {multiSelect && (
                                    <input
                                        type="checkbox"
                                        checked={selectedRows.length === data.length && data.length > 0}
                                        onChange={() => {
                                            const newSelectedRows = selectedRows.length === data.length
                                                ? []
                                                : data.map(item => item.id);
                                            setSelectedRows(newSelectedRows);
                                            onSelect(newSelectedRows);
                                        }}
                                        className="cursor-pointer"
                                    />
                                )}
                            </th>
                        )}
                        {columns.map((column) => (
                            <th
                                key={column.key}
                                className={`${cellClassName} text-left ${sortable ? 'cursor-pointer' : ''}`}
                                onClick={() => handleSort(column.key)}
                            >
                                <div className="flex items-center text-nowrap">
                                    {column.title}
                                    {sortable && getSortIcon(column.key)}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sortedData.map((row) => (
                        <tr
                            key={row.id}
                            className={`border-b ${rowClassName} ${selectedRows.includes(row.id) ? 'bg-blue-50' : ''} ${onRowClick ? 'cursor-pointer' : ''}`}
                            onClick={() => onRowClick?.(row)}
                        >
                            {selectable && (
                                <td className={cellClassName} onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        checked={selectedRows.includes(row.id)}
                                        onChange={() => handleRowSelect(row.id)}
                                        className="cursor-pointer"
                                    />
                                </td>
                            )}
                            {columns.map((column) => (
                                <td
                                    key={`${row.id}-${column.key}`}
                                    className={cellClassName}
                                    onClick={(e) => {
                                        if (column.disableRowClick) e.stopPropagation();
                                    }}
                                >
                                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                    {sortedData.length === 0 && (
                        <tr>
                            <td colSpan={columns.length + (selectable ? 1 : 0)} className={`${cellClassName} text-center text-gray-500`}>
                                No data available
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

ListDisplayTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    columns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        render: PropTypes.func,
        disableRowClick: PropTypes.bool
    })).isRequired,
    sortable: PropTypes.bool,
    selectable: PropTypes.bool,
    multiSelect: PropTypes.bool,
    defaultSort: PropTypes.shape({
        key: PropTypes.string,
        direction: PropTypes.oneOf(['ascending', 'descending'])
    }),
    onSort: PropTypes.func,
    onSelect: PropTypes.func,
    onRowClick: PropTypes.func,
    className: PropTypes.string,
    headerClassName: PropTypes.string,
    rowClassName: PropTypes.string,
    cellClassName: PropTypes.string,
    stickyHeader: PropTypes.bool
};

ListDisplayTable.defaultProps = {
    sortable: false,
    selectable: false,
    multiSelect: false,
    defaultSort: null,
    onSort: () => { },
    onSelect: () => { },
    onRowClick: null,
    className: '',
    headerClassName: 'bg-gray-200',
    rowClassName: 'hover:bg-gray-100',
    cellClassName: 'p-3',
    stickyHeader: true
};

export default ListDisplayTable;
